export type DialogController = {
    readonly dialog: HTMLDialogElement | null;
    open: () => Promise<void>;
    close: () => void;
};

export type DialogTemplateRef = Ref<DialogController | null> & DialogController;

type DialogTarget = HTMLDialogElement | null | undefined;
type DialogSource = string | DialogTarget | { value: DialogTarget };

export function useDialog(): DialogTemplateRef;
export function useDialog(source: DialogSource, model?: { value: boolean }): DialogController;
export function useDialog(source?: DialogSource, model?: { value: boolean })
{
    if (source === undefined)
    {
        const dialogRef = ref<DialogController | null>(null) as DialogTemplateRef;

        Object.defineProperties(dialogRef, {
            dialog: {
                get()
                {
                    return dialogRef.value?.dialog ?? null;
                }
            },
            open: {
                value: async () =>
                {
                    await dialogRef.value?.open();
                }
            },
            close: {
                value: () =>
                {
                    dialogRef.value?.close();
                }
            }
        });

        return dialogRef;
    }

    return createDialogController(source, model);
}

function createDialogController(source: DialogSource, model?: { value: boolean })
{
    let dialog: HTMLDialogElement | null = null;
    let boundDialog: HTMLDialogElement | null = null;

    function resolveSource()
    {
        if (typeof source === 'string')
        {
            return source;
        }

        if (source && typeof source === 'object' && 'value' in source)
        {
            return source.value;
        }

        return source;
    }

    function resolveDialog()
    {
        if (!import.meta.client)
        {
            return null;
        }

        const currentSource = resolveSource();

        if (typeof currentSource === 'string')
        {
            dialog = document.getElementById(currentSource) as HTMLDialogElement | null;
        }
        else if (currentSource instanceof HTMLDialogElement)
        {
            dialog = currentSource;
        }
        else
        {
            dialog = null;
        }

        if (model && dialog && dialog !== boundDialog)
        {
            boundDialog?.removeEventListener('close', onClose);
            boundDialog = dialog;
            boundDialog.addEventListener('close', onClose);
        }

        return dialog;
    }

    function onClose()
    {
        if (model)
        {
            model.value = false;
        }
    }

    if (model)
    {
        watch(() => model.value, async (value) =>
        {
            await nextTick();

            if (value)
            {
                open();

                return;
            }

            close();
        });

        onMounted(() =>
        {
            resolveDialog();

            if (model.value)
            {
                open();
            }
        });

        onBeforeUnmount(() =>
        {
            boundDialog?.removeEventListener('close', onClose);
        });
    }

    async function open()
    {
        const currentDialog = resolveDialog();

        if (!currentDialog || currentDialog.open)
        {
            return;
        }

        currentDialog.returnValue = '';
        currentDialog.dispatchEvent(new CustomEvent('ideo-modal-open'));
        await nextTick();
        currentDialog.showModal();
    }

    function close()
    {
        const currentDialog = resolveDialog();

        if (!currentDialog || !currentDialog.open)
        {
            return;
        }

        currentDialog.returnValue = '';
        currentDialog.close();
    }

    return {
        get dialog()
        {
            return resolveDialog();
        },
        open,
        close
    };
}
